import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import SelectionButton from 'modules/selection-button/index.js';
import PriceBoostBanner from 'modules/price-boost-banner/index.js';
import EventCardBadge from 'modules/event-cards/badge/index.js';
import CardBackground from 'modules/event-cards/background/index.js';
import withEventCardSkeleton from 'modules/event-cards/skeleton/with-event-card-skeleton.js';
import { useEventItem, useMarketItems, useEventsLoadDone } from 'modules/common/hooks';
import EventCardsTypes from 'modules/event-cards/types.js';
import SelectionCardTypes from 'modules/event-cards/selection/types.js';
import { useSelectionCardClick } from 'modules/event-cards/selection/hooks/index.js';

const { FlexPane, Text, Button, EventTimingStatus, SportAndCountryIcon } =
    MojitoPresentation.Components;
const Scoreboard = MojitoPresentation.Components.Scoreboard.default;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { EventFormatter } = MojitoPresentation.Utils;
const { utils: EventUtils } = MojitoServices.SportsContent.Events;
const VideoStreamsUtils = MojitoServices.VideoStreaming.utils;

const CardHeader = props => {
    const { event, contentConfig, isBoosted, mojitoTools } = props;
    const { config, appContext, stringResolver } = mojitoTools;
    const cardConfig = isBoosted ? contentConfig.boostedEventCard : contentConfig.genericEventCard;

    const altText =
        appContext.sportName(event.sportId) || stringResolver.resolveString('$GENERIC.DEFAULT');
    const isOutright = EventUtils.isOutrightEventType(event.eventType);
    const isRacing = EventUtils.isRaceEventType(event.eventType);
    // Outright and racing cards have no competition name of their own to show, so they fall back to
    // the event name. Every other card keeps the event name for the scoreboard below.
    const title =
        isOutright || isRacing ? EventFormatter.formatEventName(event.name) : event.typeName;

    return (
        <FlexPane class="ta-headerContainer" config={config.headerContainer}>
            <FlexPane class="ta-headerLeft" config={config.headerLeft}>
                <SportAndCountryIcon
                    sportId={event.sportId}
                    countryCode={event.countryCode}
                    countryName={event.countryName}
                    alt={altText}
                    config={contentConfig.sportCountryIcons}
                />
                <Text class="ta-headerTitle" config={cardConfig.title}>
                    {title}
                </Text>
            </FlexPane>
            {(config.showStartTime || event.details.inPlay) && (
                <EventTimingStatus
                    config={contentConfig.headerTime}
                    eventItem={event}
                    hasStreaming={VideoStreamsUtils.isStreamAvailable(event)}
                    hasClockDetails={EventUtils.hasInPlayClockDetails(event)}
                    inplayTime={EventUtils.getEventInplayTime(event)}
                />
            )}
        </FlexPane>
    );
};

const CardBody = props => {
    const { selectionName, handicapLabel, marketName, promotionText, contentConfig, mojitoTools } =
        props;
    const { config } = mojitoTools;
    const hasPromotionText = Boolean(promotionText);

    return (
        <FlexPane class="ta-selectionInfo" config={config.selectionInfo}>
            <Text class="ta-marketName" config={contentConfig.marketName.default}>
                {marketName}
            </Text>
            <Text
                class="ta-selectionName"
                config={
                    hasPromotionText
                        ? contentConfig.selectionName.withPromotionText
                        : contentConfig.selectionName.default
                }
            >
                {`${selectionName}${handicapLabel}`}
            </Text>
            {hasPromotionText && (
                <Text class="ta-promotionText" config={contentConfig.promotionText}>
                    {promotionText}
                </Text>
            )}
        </FlexPane>
    );
};

const SelectionCardAvantgarde = withEventCardSkeleton(
    props => {
        const {
            eventId,
            marketId,
            selectionId,
            mojitoTools,
            colorMode,
            shouldRequestData,
            elementRef,
        } = props;
        const { config, style, appContext, l10n } = mojitoTools;
        const mode = colorMode || config.mode;
        const contentConfig =
            mode === EventCardsTypes.COLOR_MODES.DARK
                ? style.eventCardDetailsDark
                : config.eventCardDetails;
        const event = useEventItem(eventId, shouldRequestData);
        const market = useMarketItems([marketId], shouldRequestData)[0] || {};
        const { oddsFormat } = appContext.userSettings();
        const selection = EventUtils.getSelectionFromMarket(market, selectionId);
        const handicapLabel = selection?.handicapLabel ? ` ${selection?.handicapLabel}` : '';
        const basePrice =
            selection?.basePrice && EventUtils.getOdds(selection.basePrice, oddsFormat, l10n);
        const onEventClick = useSelectionCardClick(event, appContext);

        if (!event) {
            return null;
        }

        const isBoosted = Boolean(basePrice);
        const cardConfig = isBoosted
            ? contentConfig.boostedEventCard
            : contentConfig.genericEventCard;
        const badgeConfig = isBoosted ? config.boostedCategoryBadge : config.categoryBadge;
        const badgeRowConfig = isBoosted ? style.boostedBadgeRow : config.badgeRow;
        const imgUrl = config.showCustomBackgroundImage ? props.imgUrl : undefined;
        const showBackground =
            imgUrl ||
            (config.showBackgroundSportImage &&
                !config.noBackgroundImageSports.includes(event.sportId));

        return (
            <FlexPane
                class="ta-SelectionCardAvantgarde"
                config={cardConfig.container}
                elementRef={elementRef}
            >
                {showBackground && (
                    <CardBackground
                        config={config.cardBackground}
                        sportId={event.sportId}
                        imgUrl={imgUrl}
                    />
                )}
                <FlexPane class="ta-badgeRow" config={badgeRowConfig}>
                    <EventCardBadge config={badgeConfig} />
                </FlexPane>
                <Button
                    class="ta-clickableArea"
                    onClick={onEventClick}
                    config={cardConfig.clickableArea}
                    hrefLink={NavigationPathBuilder.sportEventPath(
                        event.sportId,
                        event.id,
                        appContext.routeResolver
                    )}
                >
                    <CardHeader
                        {...props}
                        contentConfig={contentConfig}
                        event={event}
                        isBoosted={isBoosted}
                    />
                    <Scoreboard
                        event={event}
                        config={contentConfig.scoreboard}
                        isOutright={EventUtils.isOutrightEventType(event.eventType)}
                        inplayTime={EventUtils.getEventInplayTime(event)}
                        hasClockDetails={EventUtils.hasInPlayClockDetails(event)}
                    />
                </Button>
                <FlexPane class="ta-cardBodyContainer" config={config.cardBodyContainer}>
                    <CardBody
                        {...props}
                        contentConfig={contentConfig}
                        event={event}
                        handicapLabel={handicapLabel}
                    />
                    {isBoosted ? (
                        <FlexPane config={config.selectionContainer}>
                            <PriceBoostBanner
                                config={config.priceBoostBanner}
                                event={event}
                                market={market}
                                selection={selection}
                                basePrice={basePrice}
                            />
                        </FlexPane>
                    ) : (
                        <FlexPane config={config.selectionContainer}>
                            <SelectionButton
                                config={contentConfig.selectionButton}
                                selection={selection || SelectionCardTypes.BOGUS_SELECTION}
                                event={event}
                                marketStatus={market.status}
                                marketType={market.type}
                                marketId={market.id}
                            />
                        </FlexPane>
                    )}
                </FlexPane>
            </FlexPane>
        );
    },
    props => useEventsLoadDone([props.eventId]),
    config => config.eventCardDetails?.genericEventCard?.skeletonLoader
);

SelectionCardAvantgarde.getStyle = (config, mode, merge) => ({
    eventCardDetailsDark: merge(config.eventCardDetails, config.eventCardDetailsDark || {}),
    boostedBadgeRow: merge(config.badgeRow, config.boostedBadgeRow),
});

export default SelectionCardAvantgarde;
